import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import {
    RecapWrapper,
    LinkOverride,
    ArticleTitle,
    TextWrapper,
    SkeletonWrapper
} from './recap.style';
import Image from '@mlb/react-patterns/v4/components/Image/image';
import Markdown from '@mlb/react-patterns/v4/components/Markdown';

const formatInstructionSet = {
    '1x': 'q_auto:good,w_1200,ar_16:9,c_fill,g_auto,f_jpg',
    '2x': 'q_40,w_2400,ar_16:9,c_fill,g_auto,f_jpg',
    '3x': 'q_20,w_3600,ar_16:9,c_fill,g_auto,f_jpg'
};

const Recap = ({
    headline,
    blurb,
    deviceInteraction,
    templateUrl,
    recapLink,
    isDataLoaded = false,
    external = false,
    contentId,
    isMobile,
    error
}) => {
    const srcSet = useMemo(() => {
        return {
            '1x': templateUrl?.replace('{formatInstructions}', formatInstructionSet['1x']),
            '2x': templateUrl?.replace('{formatInstructions}', formatInstructionSet['2x']),
            '3x': templateUrl?.replace('{formatInstructions}', formatInstructionSet['3x'])
        };
    }, [templateUrl]);

    const handleArticlePage = useCallback((e) => {
        if (deviceInteraction.openArticle({ contentId, title: headline })) {
            e.preventDefault();
            return false;
        } else if (deviceInteraction.webview({ url: recapLink, external })) {
            e.preventDefault();
            return false;
        }
    }, [deviceInteraction, recapLink, contentId]);

    const handlers = useMemo(() => ({
        onClick: handleArticlePage
    }), [handleArticlePage]);

    if (!isDataLoaded) {
        if (error) {
            return null;
        }
        return (
            <RecapWrapper $isMobile={isMobile}>
                <SkeletonWrapper>
                    <Skeleton height='150px' width='100%' />
                </SkeletonWrapper>
                <SkeletonWrapper>
                    <Skeleton height='28px' width='100%' margin='8px' />
                    <Skeleton height='11px' width='100%' />
                    <Skeleton height='11px' width='100%' />
                </SkeletonWrapper>
            </RecapWrapper>
        );
    } else {
        if (!(headline || blurb)) {
            return null;
        }
        return (
            <RecapWrapper $isMobile={isMobile}>

                <LinkOverride
                    target={'_blank'}
                    url={recapLink}
                    handlers={handlers}
                    $isMobile={isMobile}
                    ariaLabel={headline}
                >
                    { templateUrl
                        ? <Image
                            ratio={'16:9'}
                            src={ srcSet['2x'] }
                            srcset={srcSet}
                            alt={ blurb }
                        />
                        : null }
                </LinkOverride>

                {(headline || blurb) && <TextWrapper $headline={headline}>
                    {headline &&
                        <ArticleTitle
                            target={'_blank'}
                            url={recapLink}
                            handlers={handlers}
                        >
                            {headline}
                        </ArticleTitle>
                    }

                    {blurb &&
                        <LinkOverride
                            target={'_blank'}
                            url={recapLink}
                            handlers={handlers}
                            ariaLabel={headline}
                        >
                            <Markdown>{blurb}</Markdown>
                        </LinkOverride>
                    }
                </TextWrapper>}

            </RecapWrapper>
        );
    }
};

Recap.propTypes = {
    blurb: PropTypes.string,
    headline: PropTypes.string,
    recapLink: PropTypes.string,
    deviceInteraction: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    isLoading: PropTypes.bool,
    external: PropTypes.bool,
    contentId: PropTypes.string,
    isMobile: PropTypes.bool,
    templateUrl: PropTypes.string,
    isDataLoaded: PropTypes.bool
};

export default Recap;
